import {
  boolean,
  check,
  forward,
  type InferOutput,
  nullable,
  number,
  object,
  partialCheck,
  picklist,
  pipe,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import type {
  DispatchDetailAdditionalPaymentEntity,
  ShipmentOrderAdditionalChargeEntity,
  ShipmentOrderAdditionalChargeInputHistoryEntity,
  ShipmentOrderAdditionalInputDto
} from '@/libs/api'

/**
 * 料金状態 (Sprint 7 #1857) — shimizu-pg APPROVED 指摘 1。
 *
 * - `AMOUNT`   : 金額入力 (`amount` 必須、正の整数)
 * - `INCLUDED` : 運賃込み (`amount` は null)
 *
 * BE の `ShipmentOrderAdditionalInputDto.pricingMode` と同じ enum を使う。
 */
export const PRICING_MODE = {
  AMOUNT: 'AMOUNT',
  INCLUDED: 'INCLUDED'
} as const
export type PricingMode = (typeof PRICING_MODE)[keyof typeof PRICING_MODE]

const PricingModePicklist = picklist([
  PRICING_MODE.AMOUNT,
  PRICING_MODE.INCLUDED
])

/** 端数処理 (roundType) の選択肢。付帯費用と基本運賃の請求(income)/払い(expense) で共用する */
export const RoundTypePicklist = picklist([
  'ROUND_UP',
  'ROUND_DOWN',
  'ROUND_HALF_UP'
])

/**
 * 配送依頼の付帯費用 1 行分のフォームスキーマ。
 * 請求側 (charge) / 支払側 (payment) どちらも同形。BE DTO の `ShipmentOrderAdditionalInputDto` に対応。
 *
 * カスタム制約:
 *   - `pricingMode === 'AMOUNT'` のとき `amount` は任意 (#3890: null = 金額未確定の保留)
 *   - `pricingMode !== 'AMOUNT'` のとき `amount` は null
 *
 * `useFieldArray` 用に `uniqueId` を持つ。BE 既存行は `id` (number)、新規行は `id: null`。
 */
const baseSchema = object({
  /** RHF useFieldArray の判別用 ID (BE id を持たない新規行用に prefix 付き) */
  uniqueId: string(),
  /** BE 既存行 ID。新規行は null */
  id: nullable(number()),
  /** 追加運賃カテゴリ ID (AdditionalPaymentCategory.id)。未選択は許容しない */
  additionalPaymentCategoryId: pipe(
    nullable(number()),
    check((categoryId) => categoryId !== null, VALIDATION_MESSAGES.SELECT_TYPE)
  ),
  /** 料金状態 (AMOUNT/INCLUDED) */
  pricingMode: PricingModePicklist,
  /** 金額 (税抜)。AMOUNT のときのみ入力 (null = 保留 #3890)、INCLUDED は null */
  amount: nullable(number()),
  /** 税区分マスタ ID (PJ-462 案件個別オーバーライド、null ならカスケード fallback) */
  taxCategoryId: nullable(number()),
  /** 内税フラグ (VOC_PJ-844 #3624、null ならカスケード fallback。=== true のときのみ内税) */
  isTaxInclusive: nullable(boolean()),
  /** 端数処理 (PJ-462 案件個別オーバーライド、null ならカスケード fallback) */
  roundType: nullable(RoundTypePicklist),
  /** 備考 */
  note: nullable(string())
})

export const ShipmentOrderAdditionalFormValueSchema = pipe(
  baseSchema,
  // #3890 (VOC_PJ-882): AMOUNT + amount=null は「金額は後で入れる (保留)」として許容する。
  // INCLUDED + null (運賃込み) とは pricingMode で区別される。
  // AMOUNT 以外 (INCLUDED) のとき amount は null
  check(
    (row) => row.pricingMode === PRICING_MODE.AMOUNT || row.amount == null,
    VALIDATION_MESSAGES.INPUT_AMOUNT
  ),
  // BE 側 Prisma `amount Int?` に合わせて整数のみ許可
  check(
    (row) => row.amount == null || Number.isInteger(row.amount),
    VALIDATION_MESSAGES.INTEGER
  ),
  // #4556: AMOUNT 行の税区分・端数処理は金額の有無に関わらず必須（×クリア廃止と対）。
  // INCLUDED（運賃込み）行は税設定欄自体が非表示のため対象外。内税フラグは対象外。
  forward(
    partialCheck(
      [['pricingMode'], ['taxCategoryId']],
      ({ pricingMode, taxCategoryId }) =>
        pricingMode !== PRICING_MODE.AMOUNT || taxCategoryId != null,
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['taxCategoryId']
  ),
  forward(
    partialCheck(
      [['pricingMode'], ['roundType']],
      ({ pricingMode, roundType }) =>
        pricingMode !== PRICING_MODE.AMOUNT || roundType != null,
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['roundType']
  )
)

export type ShipmentOrderAdditionalFormValue = InferOutput<
  typeof ShipmentOrderAdditionalFormValueSchema
>

/**
 * BE Entity を行単位のフォーム値に変換する。
 * 請求側 `ShipmentOrderAdditionalChargeEntity` (配送依頼単位) と
 * 支払側 `DispatchDetailAdditionalPaymentEntity` (#3541 指摘-8 で矢羽根単位へ移行) は
 * フォーム入力に必要なフィールドが同形のため共通で扱う。
 */
export const mapToShipmentOrderAdditionalFormValue = (
  entity:
    | ShipmentOrderAdditionalChargeEntity
    | DispatchDetailAdditionalPaymentEntity
): ShipmentOrderAdditionalFormValue => ({
  uniqueId: String(entity.id),
  id: entity.id,
  additionalPaymentCategoryId: entity.additionalPaymentCategoryId,
  pricingMode: entity.pricingMode,
  amount: entity.amount,
  taxCategoryId: entity.taxCategoryId,
  // 請求側 (ShipmentOrderAdditionalCharge) のみ isTaxInclusive を持つ (VOC_PJ-844 #3624)。
  // 支払側 (DispatchDetailAdditionalPayment) は別 slice (#3876 矢羽根支払側) で追加予定のため安全アクセス。
  isTaxInclusive: 'isTaxInclusive' in entity ? entity.isTaxInclusive : null,
  roundType: entity.roundType,
  note: entity.note
})

/**
 * 履歴 (#3875) の付帯費用エンティティを新規行のフォーム値へ変換する。
 *
 * 履歴復元は「新しい配送依頼を作る」ためのプリフィルなので、BE 既存行 ID は持たせず
 * `id: null` (新規行) として展開する。`uniqueId` は `history_` プレフィックスで散らす。
 */
export const mapHistoryAdditionalToFormValue = (
  entity: ShipmentOrderAdditionalChargeInputHistoryEntity,
  index: number
): ShipmentOrderAdditionalFormValue => ({
  uniqueId: `history_${index}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2)}`,
  id: null,
  additionalPaymentCategoryId: entity.additionalPaymentCategoryId,
  pricingMode: entity.pricingMode,
  amount: entity.amount,
  taxCategoryId: entity.taxCategoryId,
  isTaxInclusive: entity.isTaxInclusive,
  roundType: entity.roundType,
  note: entity.note
})

/**
 * #4268: 履歴付帯行の税メタ（税区分/内税/端数処理）が null の項目を、
 * 付帯項目 (APC) マスタの既定値で補完する版の履歴復元 mapper。
 * 履歴に値がある項目は履歴を優先する（カテゴリ選択時 pre-fill と同じ供給源に揃える）。
 */
export const mapHistoryAdditionalToFormValueWithApcFallback =
  (
    categories:
      | {
          id: number
          taxCategoryId: number | null
          isTaxInclusive: boolean
          roundType: ShipmentOrderAdditionalFormValue['roundType']
        }[]
      | undefined
  ) =>
  (
    entity: ShipmentOrderAdditionalChargeInputHistoryEntity,
    index: number
  ): ShipmentOrderAdditionalFormValue => {
    const base = mapHistoryAdditionalToFormValue(entity, index)
    const apc = categories?.find(
      (c) => c.id === entity.additionalPaymentCategoryId
    )
    return {
      ...base,
      taxCategoryId: base.taxCategoryId ?? apc?.taxCategoryId ?? null,
      isTaxInclusive: base.isTaxInclusive ?? apc?.isTaxInclusive ?? null,
      roundType: base.roundType ?? apc?.roundType ?? null
    }
  }

/**
 * 新規行追加用のフォーム値を生成する (`+付帯費用を追加` ボタン押下時)。
 *
 * デフォルトは `pricingMode='AMOUNT'`、`amount=null` (未入力扱い)。
 * カテゴリ未選択 → 保存時にバリデーションエラーになるので、ユーザに選択を強制できる。
 */
export const createInitialShipmentOrderAdditionalFormValue =
  (): ShipmentOrderAdditionalFormValue => ({
    uniqueId: `added_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    id: null,
    additionalPaymentCategoryId: null,
    pricingMode: PRICING_MODE.AMOUNT,
    amount: null,
    taxCategoryId: null,
    isTaxInclusive: null,
    roundType: null,
    note: null
  })

/**
 * Customer の `defaultAdditionalPaymentCategories` から、新規行の配列を生成する。
 * 各カテゴリの `taxCategoryId` / `roundType` は **pre-fill しない** (案件個別 override 前の初期値)。
 *
 * `pricingMode` は VOC_PJ-865 #3819 でマスタ連動に変更:
 * 各カテゴリの `isFareIncludedByDefault` が true なら **INCLUDED (運賃込み)**、
 * false なら **AMOUNT (金額入力)** で生成する。
 * (旧実装は #1857 指摘-4 で INCLUDED 固定だったが、マスタ項目ごとに
 *  運賃込みデフォルトを選べるようにするためフラグ駆動へ変更した。)
 * INCLUDED 行は amount=null でそのまま保存でき、AMOUNT 行は金額未入力のため
 * 保存時にユーザへ入力を促す。
 *
 * `uniqueId` は `default_` プレフィックスを持つ。荷主再選択時にこのプレフィックスで
 * 旧デフォルト行のみを判別して入れ替えるために使う。
 * 同じ呼び出し時刻でも `uniqueId` が衝突しないよう Math.random で散らす。
 */
export const createInitialAdditionalsFromDefaults = (
  defaults: { id: number; isFareIncludedByDefault: boolean }[]
): ShipmentOrderAdditionalFormValue[] =>
  defaults.map((cat) => ({
    uniqueId: `default_${cat.id}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`,
    id: null,
    additionalPaymentCategoryId: cat.id,
    pricingMode: cat.isFareIncludedByDefault
      ? PRICING_MODE.INCLUDED
      : PRICING_MODE.AMOUNT,
    amount: null,
    taxCategoryId: null,
    isTaxInclusive: null,
    roundType: null,
    note: null
  }))

/**
 * VOC_PJ-863 #3809: 「請求側付帯費用の項目に合わせる」ボタン押下時、請求側の
 * カテゴリID一覧から支払側の新規行配列を生成する。
 *
 * カテゴリのみコピーする仕様のため、金額・備考は持ち越さず `null` 固定、
 * 既存行との連続性もない新規行として `id: null` で生成する
 * (請求側と支払側は別テーブルのため、コピーは複製であり移動ではない)。
 * 税区分・内税・端数処理・運賃込みは、カテゴリ手動選択時の pre-fill
 * (`ShipmentOrderAdditionalRow`) と同じ APC マスタ既定値で補完する。
 */
export const buildCopiedAdditionalPaymentFormValue = (
  categoryIds: number[],
  categories:
    | {
        id: number
        taxCategoryId: number | null
        isTaxInclusive: boolean
        roundType: ShipmentOrderAdditionalFormValue['roundType']
        isFareIncludedByDefault: boolean
      }[]
    | undefined
): ShipmentOrderAdditionalFormValue[] =>
  categoryIds.map((categoryId) => {
    const apc = categories?.find((c) => c.id === categoryId)
    return {
      uniqueId: `copied_${categoryId}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2)}`,
      id: null,
      additionalPaymentCategoryId: categoryId,
      pricingMode: apc?.isFareIncludedByDefault
        ? PRICING_MODE.INCLUDED
        : PRICING_MODE.AMOUNT,
      amount: null,
      taxCategoryId: apc?.taxCategoryId ?? null,
      isTaxInclusive: apc?.isTaxInclusive ?? null,
      roundType: apc?.roundType ?? null,
      note: null
    }
  })

/**
 * フォーム値を BE `ShipmentOrderAdditionalInputDto` に変換する。
 *
 * 制約:
 *   - `pricingMode !== 'AMOUNT'` のとき `amount` は null として送信
 *   - `additionalPaymentCategoryId` は null を許容しない (バリデーションで弾く前提)
 */
export const mapShipmentOrderAdditionalFormValueToDto = (
  row: ShipmentOrderAdditionalFormValue
): ShipmentOrderAdditionalInputDto => ({
  ...(row.id != null ? { id: row.id } : {}),
  additionalPaymentCategoryId: row.additionalPaymentCategoryId as number,
  pricingMode: row.pricingMode,
  amount: row.pricingMode === PRICING_MODE.AMOUNT ? row.amount : null,
  taxCategoryId: row.taxCategoryId,
  isTaxInclusive: row.isTaxInclusive,
  roundType: row.roundType,
  note: row.note
})

/**
 * AMOUNT 行の `amount` の reduce 合計。INCLUDED 行は 0 として扱う。
 * 末尾合計表示で使用。
 */
export const sumShipmentOrderAdditionalAmounts = (
  rows: Pick<ShipmentOrderAdditionalFormValue, 'pricingMode' | 'amount'>[]
): number =>
  rows.reduce(
    (acc, row) =>
      row.pricingMode === PRICING_MODE.AMOUNT && row.amount != null
        ? acc + row.amount
        : acc,
    0
  )
